<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import OnboardingStepContainer from './OnboardingStepContainer.svelte';
	import { Alert, Checkbox, Icon, Input, Popover, Text } from '@Shelly/shelly-design-system/index';
		import { real_id } from 'src/helpers/interface_helpers';
	import { valueOutOfRange } from 'src/helpers/utilities';
	import {
		convertDistUnits,
		conversionFactors,
		DISTANCE_BASE_SIZE,
		DISTANCE_MIN_NOTATION,
	} from 'src/helpers/distance_helper';
	import LiveDistanceComponent from './LiveDistanceComponent.svelte';

	$: settingsStore = getDeviceSettings(device.id);
	export let device;
	export let deviceData;

	let error;
	let displayValue;

	$: topLabel = deviceData?.reverseMeasurement ? '0%' : '100%';
	$: bottomLabel = deviceData?.reverseMeasurement ? '100%' : '0%';
	$: min = distanceUnit === 'in' ? '7.88' : '200';
	$: max = distanceUnit === 'in' ? '196.85' : '5000';
	$: step = distanceUnit === 'in' ? '0.01' : '1';
	$: factor = conversionFactors[distanceUnit] || 1;

	$: distanceUnit = device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';

	$: installationHeight =
		deviceData.modeType === 'occupancy'
			? $format('chart_baseline_distance', {
					default: 'Baseline{br}distance',
					values: { br: '<br/>' },
				})
			: $format('chart_installation_height', {
					default: 'Installation{br}height',
					values: { br: '<br/>' },
				});

	$: meterRange = $format('chart_meter_range', {
		default: 'Meter{br}range',
		values: { br: '<br/>' },
	});

	$: originalWave = $format('chart_original_wave', {
		default: 'Original{br}wave',
		values: { br: '<br/>' },
	});

	$: reflectedWave = $format('chart_reflected_wave', {
		default: 'Reflected{br}wave',
		values: { br: '<br/>' },
	});

	$: closedDoorShutter = $format('chart_closed_position', {
		default: 'Closed{br}door/shutter',
		values: { br: '<br/>' },
	});

	$: under300mm = $format('chart_under_value', {
		default: '>{value} {unit}',
		values: { value: distanceUnit === 'in' ? '11.81' : '300', unit: convertDistUnits(distanceUnit) },
	});

	$: garageTypeValue =
		typeof deviceData.garageType === 'string' ? deviceData.garageType : deviceData.garageType?.value;

	$: floorWall =
		garageTypeValue === 'Vertical'
			? $format('chart_label_floor', { default: 'Floor' })
			: $format('chart_label_wall', { default: 'Wall' });

	$: {
		const nextError = valueOutOfRange(deviceData.installationHeight / factor, +min, +max)
			? $format('min_max_error_description', {
					values: { min, max },
					default: 'Value must be between {min} and {max}.',
				})
			: undefined;
		const nextValid = !nextError;

		if (error !== nextError) {
			error = nextError;
		}

		if (deviceData.isValid !== nextValid) {
			deviceData.isValid = nextValid;
		}
	}

	$: {
		// Convert the canonical value (in mm) to the display unit.
		const calculatedValue = deviceData.installationHeight / factor;

		// This condition is crucial. It prevents overwriting user input by only
		// updating the display if it's out of sync with the underlying data.
		if (
			displayValue === undefined ||
			Math.abs(displayValue - calculatedValue) > DISTANCE_MIN_NOTATION
		) {
			displayValue = calculatedValue.toFixed(distanceUnit === 'in' ? 2 : 0);
		}
	}

	const handleInput = (event: Event) => {
		const target = event.target as HTMLInputElement;
		const newDisplayValue = target.value;

		// Update the view immediately for a responsive feel.
		displayValue = newDisplayValue;

		// If the input is a valid number, convert it back to mm and update the data model.
		if (!isNaN(+newDisplayValue)) deviceData.installationHeight = +newDisplayValue * factor;
	};

	$: mainText =
		deviceData.modeType === 'garage'
			? $format('main_text_mount_sensor', {
					default: 'Mount the sensor on the moving door or shutter.',
				})
			: deviceData.modeType === 'occupancy'
				? $format('text_occupancy_install_height', {
						default:
							"Capture the distance when the parking spot is empty. We'll use this baseline to decide when the spot becomes Occupied vs Vacant.",
					})
				: $format('text_liquid_install_height', {
						default:
							'Set the distance from the sensor to the bottom of the container/space.',
					});
</script>

<OnboardingStepContainer>
	<svelte:fragment slot="text">
		<Text styleType="body-small" class="mb-m">{mainText}</Text>
	</svelte:fragment>

	<svelte:fragment slot="left">
		{#if deviceData.modeType === 'garage'}
			<Text styleType="body-small" class="mb-m">
				{$format('text_garage_installation_1', {
					default:
						'Aim it at a fixed, flat, reflective surface (wall, floor or a solid metal/plastic plate) with a clear line of sight for the full travel.',
				})}
			</Text>
			<Text styleType="body-small" class="mb-m text-bold">
				{$format('text_garage_installation_2', {
					default:
						'In the fully closed position, keep at least 300 mm between the sensor and the target.',
				})}
			</Text>
			<Text styleType="body-small" class="mb-m">
				{$format('text_garage_installation_3', {
					default:
						'The measurement beam spreads in roughly a 30° cone - keep this cone free of obstacles so the sensor “sees” only the chosen surface.',
				})}
			</Text>
			<Alert
				type="info"
				heading={$format('alert_heading_info_accuracy', {
					default:
						'For best accuracy, mount the sensor perpendicular to the target surface. At steep angles the sound reflects away and readings become unstable.',
				})}
			/>
		{:else}
			<Text styleType="body-small" class="mb-s text-600">
				{deviceData.modeType === 'occupancy'
					? $format('label_baseline_distance', {
							default: 'Baseline distance ({unit})',
							values: { unit: convertDistUnits(distanceUnit) },
						})
					: $format('label_installation_height', {
							default: 'Installation height ({unit})',
							values: { unit: convertDistUnits(distanceUnit) },
						})}
			</Text>
			<Input
				id="input-distance-install-height"
				type="number"
				aria-labelledby="input-distance-install-height"
				icon="far fa-ruler-horizontal"
				size="m"
				label={distanceUnit === 'in' ? '78.74' : '2000'}
				class="mb-s"
				value={displayValue}
				on:input={handleInput}
				{error}
				{step}
				{min}
				{max}
			/>
			<LiveDistanceComponent
				{device}
				{deviceData}
				{distanceUnit}
				on:click={(e) => (deviceData.installationHeight = e.detail.liveDistance)}
			/>
			<Alert
				type="info"
				heading={deviceData.modeType === 'occupancy'
					? $format('alert_info_heading_capture_baseline', {
							default:
								'Capture baseline: Sets the baseline distance from the Live distance measurement. Use only when the parking spot is empty.',
						})
					: $format('alert_info_heading_capture_height', {
							default:
								'Capture height: Sets the installation height from the Live distance measurement. Use when the container/spot is empty.',
						})}
			/>
			<div class="mt-s d-flex align-items-center">
				<Checkbox
					size="m"
					bind:checked={deviceData.measureFromBase}
					id="checkbox-ffk3jkq"
					class="mr-s"
					>{$format('checkbox_measure_from_base', {
						default: 'Measure from device base',
					})}</Checkbox
				>
				<Popover type="hover" hideCloseBtn placement="top">
					<Icon class="text-primary" icon="fas fa-circle-info" size="s" />
					<Text slot="customContent" styleType="body-extra-small">
						{$format('popover_text_measure_from_base', {
							default:
								'Adds a base offset (measure from mounting base, not sensor face). Default {value} {unit}.',
							values: {
								value:
									distanceUnit === 'in'
										? (DISTANCE_BASE_SIZE / factor).toFixed(2)
										: DISTANCE_BASE_SIZE,
								unit: convertDistUnits(distanceUnit),
							},
						})}
					</Text>
				</Popover>
			</div>
		{/if}
	</svelte:fragment>

	<svelte:fragment slot="right">
		{#if deviceData.modeType === 'garage'}
			<div class="garage-diagram-container">
				<div class="garage-diagram">
					<img
						src="./images/onboarding/distance/mounting_vertical.png"
						alt={$format('vertical_garage_diagram', {
							default: 'Vertical garage diagram',
						})}
						width="360"
						height="266"
					/>
					<span class="garage-label text-center">{@html originalWave}</span>
					<span class="garage-label text-center">{@html reflectedWave}</span>
					<span class="garage-label text-center text-nowrap">{@html closedDoorShutter}</span>
					<span class="garage-label text-center text-nowrap">{@html under300mm}</span>
					<span class="garage-label text-center">{@html floorWall}</span>
				</div>
			</div>
		{:else if deviceData.modeType === 'occupancy'}
			<div class="occupancy-diagram-container">
				<div class="occupancy-diagram">
					<img
						src="./images/onboarding/distance/step2_baseline.png"
						alt={$format('baseline_distance_diagram', {
							default: 'Baseline distance diagram',
						})}
					/>
					<span class="occupancy-label text-center">{@html installationHeight}</span>
				</div>
			</div>
		{:else}
			<div class="liquid-diagram-container">
				<div class="liquid-diagram">
					<img
						src="./images/onboarding/distance/step2_install_height.png"
						alt={$format('installation_height_diagram', {
							default: 'Installation height diagram',
						})}
					/>
					<span class="liquid-label text-center">{@html installationHeight}</span>
					<span
						class={`liquid-label text-center ${deviceData?.reverseMeasurement ? 'reverse' : ''}`}
					>
						{topLabel}
					</span>
					<span
						class={`liquid-label text-center ${deviceData?.reverseMeasurement ? 'reverse' : ''}`}
					>
						{bottomLabel}
					</span>
					<span class="liquid-label text-center">{@html meterRange}</span>
				</div>
			</div>
		{/if}
	</svelte:fragment>
</OnboardingStepContainer>

<style>
	.liquid-diagram-container,
	.occupancy-diagram-container,
	.garage-diagram-container {
		display: flex;
		justify-content: end;
	}

	.liquid-diagram {
		position: relative;
		width: 203px;
		height: 241px;
	}

	.occupancy-diagram {
		position: relative;
		width: 188px;
		height: 267px;
	}

	.garage-diagram {
		position: relative;
		width: 300px;
	}

	.liquid-label,
	.occupancy-label,
	.garage-label {
		pointer-events: none;
		position: absolute;
		font-size: 10px;
		color: #6e6e6e;
	}

	.liquid-label:nth-of-type(1),
	.occupancy-label:nth-of-type(1),
	.garage-label:nth-of-type(1),
	.garage-label:nth-of-type(4),
	.garage-label:nth-of-type(5) {
		color: #fff;
	}

	.occupancy-label:nth-of-type(1) {
		top: 46%;
		left: -24%;
	}

	.liquid-label:nth-of-type(1) {
		top: 46%;
		left: -27%;
	}
	.liquid-label:nth-of-type(2) {
		top: 31%;
		left: 14%;
	}
	.liquid-label.reverse:nth-of-type(2) {
		top: 31%;
		left: 19%;
	}
	.liquid-label:nth-of-type(3) {
		bottom: 1%;
		left: 19%;
	}
	.liquid-label.reverse:nth-of-type(3) {
		bottom: 1%;
		left: 14%;
	}
	.liquid-label:nth-of-type(4) {
		top: 58%;
		left: 13%;
	}

	.garage-label:nth-of-type(1) {
		top: 25%;
		left: 15%;
	}
	.garage-label:nth-of-type(2) {
		top: 61%;
		left: 14%;
	}
	.garage-label:nth-of-type(3) {
		top: 11%;
		left: 80%;
	}
	.garage-label:nth-of-type(4) {
		top: 61%;
		left: 86%;
	}
	.garage-label:nth-of-type(5) {
		top: 90%;
		left: 49%;
	}

	@media (max-width: 639.98px) {
		.liquid-diagram-container {
			margin-bottom: 24px;
			justify-content: center;
		}

		.occupancy-diagram-container,
		.garage-diagram-container {
			justify-content: center;
		}
	}
</style>
